import styled, { css } from 'styled-components';

/**
 * The design draws search as `.search` inside `.toolbar` — part of the table,
 * not a standalone form control. So it does NOT compose form/base.ts:
 *
 *   - it sits on --surface-alt with a --line hairline, not a --line-strong box
 *   - it takes the toolbar's button height (--h-btn), so it lines up with the
 *     buttons beside it, rather than the form-row height (--h-ctrl)
 *   - it lifts to --surface on focus
 *
 * Every call site used to pass inputStyle={{ height, borderRadius, fontSize }}
 * to reach roughly this shape. Those are gone; the geometry lives here.
 */
export const Container = styled.div`
  position: relative;
  flex: 0 1 300px;
`;

export const InputBox = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s2);
  width: 100%;
  height: var(--h-btn);
  padding: 0 var(--s3);
  background: var(--surface-alt);
  border: 1px solid var(--line);
  border-radius: var(--r-sm);
  color: var(--text-faint);
  transition:
    background-color 0.12s,
    border-color 0.12s,
    box-shadow 0.12s;

  &:focus-within {
    background: var(--surface);
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--primary-soft);
  }

  &.white {
    background: var(--surface);
  }
`;

export const Img = styled.img``;

export const Input = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0;
  background: none;
  border: none;
  font-family: var(--font-ui);
  font-size: var(--fs-body);
  font-weight: var(--fw-regular);
  line-height: var(--lh-body);
  color: var(--text);

  &::placeholder {
    color: var(--text-faint);
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    color: var(--text-faint);
    cursor: not-allowed;
  }
`;

const iconButton = css`
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  line-height: 0;

  svg {
    width: 15px;
    height: 15px;
    stroke-width: 1.8;
  }
`;

/** Decorative — the magnifier is a label, not a control. */
export const SearchButton = styled.button`
  ${iconButton}
  pointer-events: none;
  cursor: default;
`;

/**
 * Hidden while the field is empty; the design has no permanent clear affordance.
 * Falls back to always-visible if a call site omits `placeholder`.
 */
export const ClearButton = styled.button`
  ${iconButton}
  cursor: pointer;

  &:hover:not(:disabled) {
    color: var(--text-muted);
  }

  ${Input}:placeholder-shown + & {
    visibility: hidden;
  }
`;

export const Result = styled.div`
  position: absolute;
  top: calc(100% + var(--s1));
  left: 0;
  width: 100%;
  z-index: 999;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-md);
  box-shadow: var(--sh-2);
  overflow: hidden;
`;

export const ResultItems = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
`;

export const ResultItem = styled.li`
  display: flex;
  align-items: center;
  min-height: var(--h-ctrl);
  padding: 0 var(--s3);
  border-bottom: 1px solid var(--line);
  font-size: var(--fs-body);
  color: var(--text);
  cursor: pointer;

  :last-child {
    border-bottom: none;
  }

  :hover {
    background: var(--surface-alt);
  }
`;
